import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/BrandLogo';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { track } from '../utils/analytics';
import './EmailAuth.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const RESEND_SECONDS = 60;

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const getRecaptchaToken = useRecaptcha();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  // 'default' key = first entry in this tab (direct visit / new tab): nothing to go back to.
  const handleBack = () => {
    track('login_back_click', { has_history: location.key !== 'default' });
    if (location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  const handleGoogleLogin = () => {
    track('login_started', { method: 'google' });
    window.location.href = `${API_BASE}/auth/google`;
  };

  const requestLink = async (address: string) => {
    setSending(true);
    setFormError(null);
    try {
      const recaptchaToken = await getRecaptchaToken('login_link');
      const res = await fetch(`${API_BASE}/auth/email/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: address, recaptchaToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      track('login_started', { method: 'email' });
      setSentTo(address);
      setCooldown(RESEND_SECONDS);
    } catch {
      setFormError('Could not reach the server. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (address) void requestLink(address);
  };

  // Check for error in URL
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');

  return (
    <div className="login-page-fullscreen">
      <Helmet>
        <title>Sign In — ViralAI</title>
        <meta name="description" content="Sign in to ViralAI and start turning your long-form videos into viral shorts automatically." />
        <link rel="canonical" href="https://www.vimapi.com/login" />
      </Helmet>
      <div className="login-container">
        <button type="button" className="login-back" onClick={handleBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M20 12H4M11 5l-7 7 7 7" />
          </svg>
          Back
        </button>
        <div className="login-header">
          <Link to="/" className="login-logo-link" onClick={() => track('login_home_click', { target: 'vimapi' })}>
            <BrandLogo variant="onlight" height={48} className="login-logo" />
          </Link>
          <h1>Welcome to ViralAI</h1>
        </div>

        {error && (
          <div className="login-error-banner">
            {error === 'not_on_waitlist'
              ? 'This email is not on the waitlist yet. Sign-up is currently limited to waitlist members.'
              : 'Authentication failed. Please try again.'}
          </div>
        )}

        {sentTo ? (
          <div className="ea-sent" role="status">
            <h2>Check your inbox</h2>
            <p>
              If an account exists for <strong>{sentTo}</strong>, we have sent a sign-in link. It
              works once and expires in 15 minutes.
            </p>
            <button
              type="button"
              className="ea-btn-link"
              disabled={sending || cooldown > 0}
              onClick={() => void requestLink(sentTo)}
            >
              {cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend link'}
            </button>
            {formError && <div className="ea-error" role="alert">{formError}</div>}
            <button
              type="button"
              className="ea-btn-link"
              onClick={() => { setSentTo(null); setFormError(null); }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form className="ea-form" onSubmit={handleEmailSubmit} noValidate>
            <div className="ea-field">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="ea-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {formError && <div className="ea-error" role="alert">{formError}</div>}
            <button type="submit" className="ea-btn" disabled={sending || !email.trim()}>
              {sending ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}

        <div className="ea-divider" role="separator">or</div>

        <button
          className="google-signin-btn"
          onClick={handleGoogleLogin}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="24" height="24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <a
          className="login-whatsapp"
          href="https://wa.me/919220891200"
          target="_blank"
          rel="noopener noreferrer"
        >
          Need help? WhatsApp us
        </a>

        <p className="ea-switch">
          New here? <Link to="/signup">Create an account</Link>
        </p>

        <p className="login-terms">
          By proceeding, I agree to{' '}
          <a href="/terms">T&C</a> &{' '}
          <a href="/privacy">Privacy Policy</a>
        </p>
        <p className="login-terms">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
        </p>

        <nav className="login-sitelinks" aria-label="Sites">
          <Link to="/" onClick={() => track('login_home_click', { target: 'vimapi_footer' })}>vimapi.com home</Link>
          <span aria-hidden="true">·</span>
          <a
            href="https://www.viralai.tech"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('login_home_click', { target: 'viralai_tech' })}
          >
            viralai.tech home
          </a>
        </nav>
      </div>
    </div>
  );
};

export default LoginPage;
